// src/prototype/enterprise/portfolio/PortfolioSurface.tsx
// ============================================================
// ENTERPRISE · PORTFOLIO SURFACE (Surface A) — the living portfolio, wired to the spine.
//
// Wired LIVE to the spine via its adapter functions only (getRuns / getRunCap /
// getPortfolioAsOf / getDecisionLog / getChangeDelta) — never raw mock rows, never modifying
// the spine. Scoring (rank, 4 dimensions, composite, confidence), owner + category all come
// from getPortfolioAsOf; status is derived from getDecisionLog; over-time movement from
// getChangeDelta. Layout/styling match Enterprise Prototype.dc.html · Surface A.
//
// Parts:
//  · Run timeline (1e): the 3 runs; selecting one re-scores the grid (getPortfolioAsOf) and
//    updates the shared as-of run (navy stamp follows via useEnterpriseRun).
//  · Org rollup (1c): per-service-line cards (count + in-pipeline) above the grid.
//  · Delta mode (1d): "what changed since Q1" — rose / fell / crossed-below / status-change
//    chips (getChangeDelta) + the Δ Q1 rank-move column.
//  · evolution → : per-row affordance, inert for now (the evolution view is Phase 5).
// ============================================================

import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import {
  getRuns, getRunCap, getPortfolioAsOf, getDecisionLog, getChangeDelta,
} from '../../data/adapters/enterpriseSpine';
import { useEnterpriseRun } from '../useEnterpriseRun';
import { EvolutionView } from '../evolution/EvolutionView';
import {
  initiativeName, statusByUseCase,
  STATUS_BADGE, confidenceBadge, type DisplayStatus,
} from './portfolioDisplay';

interface Row {
  use_case_id: number;
  rank: number;
  move: number; // rank delta vs Q1 baseline (positive ⇒ rose)
  name: string;
  category: string;
  fi: number;
  ou: number;
  sa: number;
  ifs: number;
  composite: number;
  confidence: number; // %
  ownerName: string;
  ownerOrg: string;
  status: DisplayStatus;
}

type SortKey = 'rank' | 'name' | 'fi' | 'ou' | 'sa' | 'ifs' | 'composite' | 'confidence' | 'owner' | 'status';

// Grid columns match the design: Rank · Δ Q1 · Initiative · Fin · OpUrg · Strat · Feas · Comp
// · Conf · Owner · Status · evolution→
const GRID_COLS = '44px 56px minmax(220px,1fr) 50px 50px 50px 50px 66px 56px 150px 100px 80px';
const GRID_MIN = 1150;

const COLS: { key: string; label: string; sort?: SortKey; center?: boolean }[] = [
  { key: 'rank', label: 'Rank', sort: 'rank' },
  { key: 'move', label: 'Δ Q1' },
  { key: 'name', label: 'Initiative', sort: 'name' },
  { key: 'fi', label: 'Fin', sort: 'fi', center: true },
  { key: 'ou', label: 'OpUrg', sort: 'ou', center: true },
  { key: 'sa', label: 'Strat', sort: 'sa', center: true },
  { key: 'ifs', label: 'Feas', sort: 'ifs', center: true },
  { key: 'composite', label: 'Comp', sort: 'composite', center: true },
  { key: 'confidence', label: 'Conf', sort: 'confidence', center: true },
  { key: 'owner', label: 'Owner', sort: 'owner' },
  { key: 'status', label: 'Status', sort: 'status' },
  { key: 'evo', label: '' },
];

const sortValue = (r: Row, key: SortKey): number | string => {
  switch (key) {
    case 'name': return r.name.toLowerCase();
    case 'owner': return r.ownerName.toLowerCase();
    case 'status': return r.status;
    default: return r[key] as number;
  }
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (iso: string): string => `${MONTHS[+iso.slice(5, 7) - 1]} ${+iso.slice(8, 10)} ${iso.slice(0, 4)}`;
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

export const PortfolioSurface: React.FC = () => {
  const { runId, setRunId } = useEnterpriseRun();
  const [showDelta, setShowDelta] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [asc, setAsc] = useState(true);
  const [evoId, setEvoId] = useState<number | null>(null);

  // Run timeline model — one card per run, sub-copy/decisions derived from the spine.
  const runs = useMemo(() => getRuns(), []);
  const q1Id = runs[0]?.run_id ?? '';
  const activeRunId = runId || q1Id;
  const isBaselineRun = activeRunId === q1Id;
  const deltaOn = showDelta && !isBaselineRun;

  const runCards = useMemo(
    () =>
      runs.map((run, idx) => {
        const asof = getPortfolioAsOf(run.run_id);
        const funded = asof?.rollup.funded_count ?? 0;
        const count = asof?.initiatives.length ?? 0;
        const cap = getRunCap(run.run_id);
        const decN = getDecisionLog({ run_id: run.run_id }).length;
        const eyebrow = run.scenario_id ? 'Scenario run' : idx === 0 ? 'Baseline run' : 'Actual run';
        const date = fmtDate(run.created_at);
        const sub =
          eyebrow === 'Baseline run'
            ? `${date} · ${count} scored · ${funded} funded · cap $${cap.toFixed(1)}M`
            : eyebrow === 'Scenario run'
              ? `${date} · cap $${cap.toFixed(1)}M · ${funded} funded`
              : `${date} · re-scored with observed data`;
        const dec = eyebrow === 'Actual run' && decN === 0 ? 'confidence up portfolio-wide' : `${plural(decN, 'decision')} logged`;
        return { run_id: run.run_id, eyebrow, label: run.label, sub, dec };
      }),
    [runs],
  );

  // The grid for the currently selected run + its Δ-vs-Q1 movement and org rollup.
  const { rows, runLabel, rollup, chips } = useMemo(() => {
    const asof = getPortfolioAsOf(activeRunId);
    const statusMap = statusByUseCase(getDecisionLog());
    const q1Rank = new Map<number, number>(
      (getPortfolioAsOf(q1Id)?.initiatives ?? []).map((i) => [i.use_case_id, i.rank]),
    );
    const built: Row[] = (asof?.initiatives ?? []).map((i) => ({
      use_case_id: i.use_case_id,
      rank: i.rank,
      move: (q1Rank.get(i.use_case_id) ?? i.rank) - i.rank,
      name: initiativeName(i.title),
      category: i.category,
      fi: Math.round(i.dimension_scores.financial_impact),
      ou: Math.round(i.dimension_scores.operational_urgency),
      sa: Math.round(i.dimension_scores.strategic_alignment),
      ifs: Math.round(i.dimension_scores.implementation_feasibility),
      composite: i.composite,
      confidence: Math.round(i.confidence * 100),
      ownerName: i.owner.name,
      ownerOrg: i.owner.org,
      status: statusMap.get(i.use_case_id) ?? 'Active',
    }));

    // Org rollup: count + funded ("in pipeline") per service line, biggest first.
    const byOrg = new Map<string, { org: string; count: number; pipeline: number }>();
    const fundedIds = new Set((asof?.initiatives ?? []).filter((i) => i.funded).map((i) => i.use_case_id));
    for (const i of asof?.initiatives ?? []) {
      const cell = byOrg.get(i.owner.org) ?? { org: i.owner.org, count: 0, pipeline: 0 };
      cell.count += 1;
      if (fundedIds.has(i.use_case_id)) cell.pipeline += 1;
      byOrg.set(i.owner.org, cell);
    }
    const rollupCells = [...byOrg.values()].sort((a, b) => b.count - a.count);

    // Delta chips vs Q1 (only meaningful off the baseline).
    const delta = isBaselineRun ? null : getChangeDelta(q1Id, activeRunId);
    const statusChanges = getDecisionLog({ run_id: activeRunId }).length;
    const chipData = delta
      ? { rose: delta.summary.risers, fell: delta.summary.fallers, crossed: delta.summary.dropped_below, statusChanges }
      : null;

    return { rows: built, runLabel: asof?.run.label ?? '', rollup: rollupCells, chips: chipData };
  }, [activeRunId, q1Id, isBaselineRun]);

  const sorted = useMemo(() => {
    const dir = asc ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return a.rank - b.rank;
    });
  }, [rows, sortKey, asc]);

  const onSort = (key: SortKey) => {
    if (key === sortKey) setAsc((v) => !v);
    else { setSortKey(key); setAsc(key === 'name' || key === 'owner' || key === 'status'); }
  };

  const selectRun = (id: string) => {
    setRunId(id);
    if (id === q1Id) setShowDelta(false); // no Δ against the baseline itself
  };

  return (
    <div className="min-h-[calc(100vh-180px)] bg-[#F5F7FA]">
      <div className="px-6 py-6">
        {/* Run timeline (1e) + delta toggle */}
        <div className="flex flex-wrap items-stretch gap-3 mb-3.5">
          {runCards.map((rc) => {
            const active = rc.run_id === activeRunId;
            return (
              <button
                key={rc.run_id}
                onClick={() => selectRun(rc.run_id)}
                className={`flex-1 min-w-[220px] text-left bg-white border border-[#e5e7eb] rounded-[10px] px-4 py-3 transition-shadow ${
                  active ? 'outline outline-2 outline-[#FF6E4C]' : 'hover:shadow-sm'
                }`}
              >
                <div className={`text-[9px] font-bold uppercase tracking-[0.1em] ${active ? 'text-[#FF6E4C]' : 'text-[#717182]'}`}>
                  {rc.eyebrow}
                </div>
                <div className="text-[13.5px] font-bold text-[#18365E] mt-0.5">{rc.label}</div>
                <div className="text-[10.5px] text-[#717182] mt-1">{rc.sub}</div>
                <div className="text-[10.5px] text-[#15803d] mt-0.5">{rc.dec}</div>
              </button>
            );
          })}
          {!isBaselineRun && (
            <div className="flex-none flex flex-col justify-center">
              <button
                onClick={() => setShowDelta((v) => !v)}
                className={`rounded-full px-3.5 py-2 text-[11px] font-semibold transition-colors ${
                  deltaOn ? 'bg-[#18365E] text-white' : 'bg-white border border-[#18365E] text-[#18365E] hover:bg-[#18365E]/[0.04]'
                }`}
              >
                {deltaOn ? 'Hide changes' : 'Show changes since Q1'}
              </button>
            </div>
          )}
        </div>

        {/* Org rollup (1c) — above the grid */}
        <div className="flex flex-wrap gap-2.5 mb-3.5">
          {rollup.map((r) => (
            <div key={r.org} className="bg-white border border-[#e5e7eb] rounded-lg px-3 py-2 text-center min-w-[92px]">
              <div className="text-[8.5px] font-bold uppercase tracking-[0.06em] text-[#717182]">{r.org}</div>
              <div className="text-[15px] font-bold text-[#18365E] leading-tight">{r.count}</div>
              <div className="text-[8.5px] text-[#b45309]">{r.pipeline} in pipeline</div>
            </div>
          ))}
        </div>

        {/* Delta chips (1d) */}
        {deltaOn && chips && (
          <div className="flex flex-wrap items-center gap-2.5 mb-3.5">
            <span className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-[#15803d] bg-[#dcfce7]">▲ {chips.rose} rose</span>
            <span className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-[#b91c1c] bg-[#fee2e2]">▼ {chips.fell} fell</span>
            <span className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-[#b45309] bg-[#fef3c7]">⤓ {chips.crossed} crossed below funding line</span>
            <span className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-[#475569] bg-[#e2e8f0]">{plural(chips.statusChanges, 'status change')}</span>
            <span className="ml-auto text-[10.5px] text-[#717182]">Movements link to the decisions that caused them → Decisions tab</span>
          </div>
        )}

        {/* Grid */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-[#e5e7eb]">
            <div className="text-base font-bold text-[#18365E]">Enterprise Portfolio</div>
            <div className="text-[11px] text-[#717182] mt-0.5">
              {rows.length} initiatives · system of record · viewing as of{' '}
              <b className="text-[#18365E]">{runLabel}</b>
              {deltaOn && ' · showing Δ vs Q1 baseline'}
            </div>
          </div>

          <div className="overflow-x-auto">
            <div style={{ minWidth: GRID_MIN }}>
              {/* Header row */}
              <div
                className="grid items-center px-5 py-2 border-b border-[#e5e7eb] text-[9px] font-bold uppercase tracking-[0.07em] text-[#717182]"
                style={{ gridTemplateColumns: GRID_COLS, columnGap: 8 }}
              >
                {COLS.map((c) => {
                  if (!c.sort) return <div key={c.key} className={c.center ? 'text-center' : ''}>{c.label}</div>;
                  const active = c.sort === sortKey;
                  return (
                    <button
                      key={c.key}
                      onClick={() => onSort(c.sort!)}
                      className={`flex items-center gap-0.5 uppercase ${c.center ? 'justify-center' : ''} ${active ? 'text-[#18365E]' : 'hover:text-[#18365E]'}`}
                    >
                      {c.label}
                      {active && (asc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                    </button>
                  );
                })}
              </div>

              {/* Data rows */}
              {sorted.map((r, i) => (
                <div
                  key={r.use_case_id}
                  className="grid items-center px-5 py-2 border-b border-[#f1f3f6]"
                  style={{ gridTemplateColumns: GRID_COLS, columnGap: 8, background: i % 2 ? '#FAFBFC' : '#fff' }}
                >
                  <div className="w-[25px] h-[25px] rounded-full bg-[#18365E] text-white text-[10.5px] font-bold flex items-center justify-center">
                    {r.rank}
                  </div>
                  <div
                    className="text-[10px] font-bold"
                    style={{ color: r.move > 0 ? '#15803d' : r.move < 0 ? '#b91c1c' : '#c3c9d2' }}
                  >
                    {deltaOn ? (r.move > 0 ? `▲ ${r.move}` : r.move < 0 ? `▼ ${-r.move}` : '—') : ''}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-[#18365E] truncate">{r.name}</div>
                    <div className="text-[9.5px] text-[#9ca3af] truncate">{r.category}</div>
                  </div>
                  <div className="text-center text-[11.5px] text-[#374151]">{r.fi}</div>
                  <div className="text-center text-[11.5px] text-[#374151]">{r.ou}</div>
                  <div className="text-center text-[11.5px] text-[#374151]">{r.sa}</div>
                  <div className="text-center text-[11.5px] text-[#374151]">{r.ifs}</div>
                  <div className="text-center text-[12.5px] font-bold text-[#18365E]">{r.composite.toFixed(1)}</div>
                  <div className="text-center">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${confidenceBadge(r.confidence)}`}>
                      {r.confidence}%
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10.5px] text-[#374151] truncate">{r.ownerName}</div>
                    <div className="text-[9px] text-[#9ca3af] truncate">{r.ownerOrg}</div>
                  </div>
                  <div>
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${STATUS_BADGE[r.status]}`}>
                      {r.status}
                    </span>
                  </div>
                  <button
                    onClick={() => setEvoId(r.use_case_id)}
                    className="text-[10px] font-semibold text-[#18365E] underline decoration-dotted underline-offset-2 hover:text-[#FF6E4C] text-left"
                    title="View initiative evolution"
                  >
                    evolution →
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="px-5 py-2.5 text-[10.5px] text-[#9ca3af] bg-[#F5F7FA]">
            Showing all {rows.length} · sortable on every column · runs are immutable snapshots (scoring_run)
          </div>
        </div>
      </div>
      {evoId != null && <EvolutionView useCaseId={evoId} onClose={() => setEvoId(null)} />}
    </div>
  );
};

export default PortfolioSurface;
